import type { LucideIcon } from 'lucide-react';
import {
  ArrowUpFromLine,
  Box,
  BrickWall,
  DoorOpen,
  Eraser,
  Frame,
  Grid2X2,
  Minus,
  MousePointer2,
  Move,
  PaintBucket,
  RotateCw,
  Ruler,
  Scaling,
  Square,
} from 'lucide-react';
import type { ToolId } from '../model/types';
import { useStudioStore } from '../state/useStudioStore';

interface ToolDefinition {
  id: ToolId;
  label: string;
  shortcut: string;
  icon: LucideIcon;
}

const groups: ToolDefinition[][] = [
  [
    { id: 'select', label: 'Select', shortcut: 'V', icon: MousePointer2 },
  ],
  [
    { id: 'wall', label: 'Wall', shortcut: 'W', icon: BrickWall },
    { id: 'room', label: 'Room', shortcut: 'Q', icon: Grid2X2 },
    { id: 'slab', label: 'Slab', shortcut: 'F', icon: Square },
    { id: 'box', label: 'Box', shortcut: 'B', icon: Box },
    { id: 'line', label: 'Line', shortcut: 'L', icon: Minus },
  ],
  [
    { id: 'door', label: 'Door', shortcut: 'D', icon: DoorOpen },
    { id: 'window', label: 'Window', shortcut: 'N', icon: Frame },
  ],
  [
    { id: 'pushpull', label: 'Push / Pull', shortcut: 'P', icon: ArrowUpFromLine },
    { id: 'move', label: 'Move', shortcut: 'M', icon: Move },
    { id: 'rotate', label: 'Rotate', shortcut: 'R', icon: RotateCw },
    { id: 'scale', label: 'Scale', shortcut: 'S', icon: Scaling },
    { id: 'paint', label: 'Paint', shortcut: 'G', icon: PaintBucket },
    { id: 'measure', label: 'Measure', shortcut: 'T', icon: Ruler },
    { id: 'eraser', label: 'Erase', shortcut: 'E', icon: Eraser },
  ],
];

export function ToolPalette(): JSX.Element {
  const activeTool = useStudioStore((state) => state.activeTool);
  const setActiveTool = useStudioStore((state) => state.setActiveTool);

  return (
    <aside className="tool-palette" aria-label="Modelling tools">
      {groups.map((group, groupIndex) => (
        <div className="tool-group" key={groupIndex}>
          {group.map(({ id, label, shortcut, icon: Icon }) => (
            <button
              key={id}
              className={`tool-button ${activeTool === id ? 'is-active' : ''}`}
              type="button"
              onClick={() => setActiveTool(id)}
              title={`${label} (${shortcut})`}
              aria-label={`${label}, shortcut ${shortcut}`}
              aria-pressed={activeTool === id}
            >
              <Icon size={20} strokeWidth={1.8} />
              <span className="tool-shortcut">{shortcut}</span>
            </button>
          ))}
        </div>
      ))}
    </aside>
  );
}
